import { useId, type ReactElement } from 'react'
import { Field as BaseField } from '@base-ui/react/field'
import { Select as BaseSelect } from '@base-ui/react/select'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FieldMessages, LabelRow, type FieldLabelProps, type FieldMessageProps, type FieldSize, type LabelAccessoryProps } from './Field'
import './select.css'

export type SelectItem<Value extends string> = {
  value: Value
  /** Plain text: the option's accessible name, its typeahead text and what the trigger shows once chosen. */
  label: string
  disabled?: boolean
  /** A Lucide icon before the label, in the list and in the trigger once chosen, such as a role glyph. Decorative. */
  icon?: ReactElement
  /** A short line under the label in the list, read as the option's description. The trigger shows only the label. */
  description?: string
}

export type SelectProps<Value extends string> = Omit<FieldMessageProps, 'validate' | 'validationMode'> & FieldLabelProps & LabelAccessoryProps & {
  items: SelectItem<Value>[]
  value?: Value | null
  defaultValue?: Value | null
  onValueChange?: (value: Value | null) => void
  /** Shown until something is chosen. */
  placeholder?: string
  /** A Lucide icon at the start of the trigger, like TextField's. Decorative. A chosen item's own icon replaces it. */
  icon?: ReactElement
  /** Compact: the trigger looks 36px, like a compact Button or TextField, and keeps its 44px target. For dense rows
   *  such as a table's; forms stay default size. The list's options keep 44px either way. */
  size?: FieldSize
  name?: string
  disabled?: boolean
  required?: boolean
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
  className?: string
}

/** One choice from a list too long to show as radios. The list opens below the field. */
export function Select<Value extends string>({
  label, hideLabel, labelAccessory, description, optional, error, requiredMessage, items, value, defaultValue, onValueChange, placeholder,
  icon, size = 'default', name, disabled, required, container, className,
}: SelectProps<Value>) {
  const descriptionIdPrefix = useId()
  // Without icons the trigger keeps Base UI's own value text; with them it renders the chosen item's icon and label.
  const withIcons = Boolean(icon) || items.some((item) => item.icon)
  const renderValue = (current: Value | null) => {
    const item = items.find((candidate) => candidate.value === current)
    const glyph = item?.icon ?? icon
    return (
      <>
        {glyph && (
          <span className={cn('play-select__glyph', !item?.icon && 'play-select__glyph--field')} aria-hidden="true">{glyph}</span>
        )}
        <span className="play-select__text">{item ? item.label : placeholder}</span>
      </>
    )
  }
  return (
    <BaseField.Root name={name} disabled={disabled} invalid={error ? true : undefined} className={cn('play-form-field', className)}>
      <BaseSelect.Root
        items={items}
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(next as Value | null)}
        required={required}
        disabled={disabled}
      >
        <LabelRow block="play-form-field" accessory={labelAccessory}>
          <BaseSelect.Label className={cn('play-form-field__label', hideLabel && 'play-form-field__label--hidden')}>
            {label}
            {optional && <span className="play-form-field__optional"> (optional)</span>}
          </BaseSelect.Label>
        </LabelRow>
        <BaseSelect.Trigger className={cn('play-field play-select__trigger', size === 'compact' && 'play-field--compact')}>
          {withIcons ? (
            <BaseSelect.Value className="play-select__value play-select__value--with-icon" placeholder={placeholder}>
              {renderValue}
            </BaseSelect.Value>
          ) : (
            <BaseSelect.Value className="play-select__value" placeholder={placeholder} />
          )}
          <BaseSelect.Icon className="play-select__icon"><ChevronDown aria-hidden="true" /></BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal container={container ?? undefined}>
          <BaseSelect.Positioner className="play-select__positioner" sideOffset={6} alignItemWithTrigger={false} collisionPadding={12}>
            <BaseSelect.Popup className="play-theme play-select__popup">
              <BaseSelect.List className="play-select__list">
                {items.map((item, index) => (
                  <BaseSelect.Item
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                    className="play-select__item"
                    {...(item.description ? { 'aria-label': item.label, 'aria-describedby': `${descriptionIdPrefix}-${index}` } : {})}
                  >
                    {item.icon || item.description ? (
                      <ItemContent item={item} descriptionId={`${descriptionIdPrefix}-${index}`} />
                    ) : (
                      <BaseSelect.ItemText>{item.label}</BaseSelect.ItemText>
                    )}
                    <BaseSelect.ItemIndicator className="play-select__check"><Check aria-hidden="true" /></BaseSelect.ItemIndicator>
                  </BaseSelect.Item>
                ))}
              </BaseSelect.List>
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
      <FieldMessages description={description} error={error} requiredMessage={requiredMessage} />
    </BaseField.Root>
  )
}

/** An option with its icon and description. The option is named by its label alone and described by the line. */
function ItemContent<Value extends string>({ item, descriptionId }: { item: SelectItem<Value>; descriptionId: string }) {
  return (
    <>
      {item.icon && <span className="play-select__item-icon" aria-hidden="true">{item.icon}</span>}
      <span className="play-select__item-text">
        <BaseSelect.ItemText className="play-select__item-label">{item.label}</BaseSelect.ItemText>
        {item.description && <span id={descriptionId} className="play-select__item-description">{item.description}</span>}
      </span>
    </>
  )
}
